import { windowEndOf } from './leadRounds';
import type { WindowUnit } from './leadRounds';
import type { LeadLoop } from './leadLoop';
import { leadStageKey } from './stageKeys';
import type { Lead, LeadStage } from './types';

/**
 * What starting a round for a set of leads would actually do — worked out
 * BEFORE anything is written.
 *
 * ── Why this is a module and not a few lines in the component ──────────────
 *
 * Because the last version of this logic was wrong in production. On 31 Aug a
 * bulk enrolment written straight into SQL excluded CLOSED leads and not
 * CONVERTED ones, and put eight live deals into a four-email chase sequence.
 * Nothing on screen said so, because nothing was computing what would happen —
 * the work just ran.
 *
 * Kyu's requirement for the replacement, in his words: "we just want the
 * system to have a clear, not confusing mechanism." Clear means the panel can
 * say, before you press anything, exactly who is being enrolled, who is being
 * skipped, why, what the daily sending load will be, and when the tasks land.
 * All five of those are this file.
 *
 * ── The rules are duplicated in the database on purpose ────────────────────
 *
 * The RPC that does the writing applies the same exclusions. That is not an
 * oversight: a client can be out of date, and a bulk write that trusts the
 * browser's idea of who is eligible is one stale tab away from the same
 * accident. This module exists to TELL somebody what will happen; the database
 * is what makes it true.
 */

export type SkipReason =
  'already-in-a-round' | 'converted-to-deal' | 'closed' | 'not-found' | 'refused';

/**
 * Plain words for the panel. The component never spells these itself.
 *
 * The keys are the strings `start_rounds_for_leads` returns, so the panel that
 * PREDICTS a skip and the result that REPORTS one speak the same vocabulary
 * and can be read side by side.
 *
 * `refused` is the one this module never produces. The database returns it for
 * a race — two people pressing at the same second — or a rule the planner does
 * not know about. It is here so such a lead comes back with a reason rather
 * than disappearing between the count on the button and the rows on screen.
 */
export const SKIP_LABELS: Record<SkipReason, string> = {
  'already-in-a-round': 'already in a running round',
  'converted-to-deal': 'Converted to Deal — chasing a live deal',
  closed: 'closed',
  'not-found': 'no longer in the list',
  refused: 'the Hub turned it down — try again',
};

export interface EnrolPlan {
  /**
   * Leads that would get a round, with the number each would be given and how
   * much of it is already done.
   *
   * `alreadySent` is the touches the round will claim on the way in — Drop 46.
   * It is per lead because it varies per lead, and the panel lists it so
   * "starts at 4 of 7" is visible before anybody presses anything.
   */
  starting: { leadId: string; number: number; alreadySent: number }[];
  /** Everybody else, grouped so the panel can say how many and why. */
  skipped: { reason: SkipReason; leadIds: string[] }[];
  /** How many leads were selected in the first place. */
  selected: number;
  /**
   * Sends this round still asks for, across every lead being enrolled.
   *
   * What is LEFT, not leads × follow-ups. A selection where half have already
   * been emailed four times asks for far less than the multiplication says,
   * and overstating it talks somebody out of a round they could finish
   * comfortably — the same failure as staying quiet about one they cannot.
   */
  totalSends: number;
  /** Touches the rounds will claim on the way in, across the selection. */
  alreadySent: number;
  /**
   * Sends per day if the round is finished on time, rounded up.
   *
   * The number that stops somebody enrolling 133 people into a five-day round.
   * That is 532 sends over 5 days — 107 a day — which nobody will do, so the
   * round is unfinishable the moment it starts and every lead in it reads as
   * running out of days by Friday.
   */
  sendsPerDay: number;
  /** The day these rounds close, and therefore the day their tasks land. */
  endsOn: string;
}

export interface RoundShape {
  startsOn: string;
  touchTarget: number;
  windowLength: number;
  windowUnit: WindowUnit;
}

/**
 * Work out what would happen. Pure: reads, decides, writes nothing.
 *
 * `runningRounds` is keyed by lead id and holds only rounds that have not
 * ended — the same map the Rounds column already reads, so the panel and the
 * table can never disagree about who is mid-round.
 */
export function planEnrolment(
  leads: readonly Lead[],
  selectedIds: readonly string[],
  stages: readonly LeadStage[],
  runningRounds: ReadonlyMap<string, unknown>,
  loops: ReadonlyMap<string, LeadLoop>,
  shape: RoundShape,
  /**
   * Touches each lead has that the round will claim — Drop 46's adoption,
   * counted per lead.
   *
   * Optional and defaulting to none, so every caller that predates adoption
   * keeps the arithmetic it had. Capped at the target per lead: a lead with
   * nine unclaimed emails against a target of seven owes zero, never minus
   * two, and a negative would quietly cancel out another lead's real work.
   */
  adopted: ReadonlyMap<string, number> = new Map(),
): EnrolPlan {
  const byId = new Map(leads.map((lead) => [lead.id, lead]));
  const starting: { leadId: string; number: number; alreadySent: number }[] = [];
  const skips = new Map<SkipReason, string[]>();
  const skip = (reason: SkipReason, leadId: string) => {
    skips.set(reason, [...(skips.get(reason) ?? []), leadId]);
  };

  for (const leadId of selectedIds) {
    const lead = byId.get(leadId);
    if (lead === undefined) {
      // Selected, then filtered out from under the selection — a real state,
      // because the tick boxes survive a filter change.
      skip('not-found', leadId);
      continue;
    }
    const stage = leadStageKey(lead.stage_id, [...stages]);
    // Converted FIRST, and named separately from closed. These are two
    // different mistakes: enrolling a closed lead schedules work against a
    // decision somebody made, while enrolling a converted one chases somebody
    // the team is already negotiating with. A panel that lumped them together
    // as "not eligible" would have hidden the worse of the two.
    if (stage === 'converted') {
      skip('converted-to-deal', leadId);
      continue;
    }
    if (stage === 'closed') {
      skip('closed', leadId);
      continue;
    }
    if (runningRounds.has(leadId)) {
      // The database refuses a second running round anyway — the partial
      // unique index sees to that. Skipping here means the panel can say so
      // instead of the write failing halfway with a constraint name.
      skip('already-in-a-round', leadId);
      continue;
    }
    // The number is not chosen, it follows. Rounds run 1, 2, 3… per lead, and
    // `roundsRun` already counts the ones that have ended.
    const ran = loops.get(leadId)?.roundsRun ?? 0;
    starting.push({
      leadId,
      number: ran + 1,
      alreadySent: Math.min(adopted.get(leadId) ?? 0, shape.touchTarget),
    });
  }

  const days = shape.windowUnit === 'weeks' ? shape.windowLength * 7 : shape.windowLength;
  const alreadySent = starting.reduce((sum, entry) => sum + entry.alreadySent, 0);
  const totalSends = starting.length * shape.touchTarget - alreadySent;
  return {
    starting,
    alreadySent,
    skipped: [...skips.entries()]
      .map(([reason, leadIds]) => ({ reason, leadIds }))
      // Stable order so the panel does not reshuffle its own rows between
      // renders while somebody is reading them.
      .sort((a, b) => a.reason.localeCompare(b.reason)),
    selected: selectedIds.length,
    totalSends,
    sendsPerDay: days === 0 ? 0 : Math.ceil(totalSends / days),
    endsOn: windowEndOf(shape.startsOn, shape.windowLength, shape.windowUnit),
  };
}

/**
 * What the confirm button says.
 *
 * Kyu's first question on reading the mockup was "Start 20 rounds — where did
 * that come from?", with 23 selected. The count was right and the gap was
 * left for him to reconstruct from a table above it. A button that makes the
 * reader do subtraction is the confusion this whole panel exists to prevent,
 * so the gap goes on the button.
 */
export function confirmLabel(plan: EnrolPlan): string {
  const n = plan.starting.length;
  if (n === 0) {
    return 'Nothing to start';
  }
  const skipped = plan.selected - n;
  const rounds = `Start ${String(n)} round${n === 1 ? '' : 's'}`;
  if (skipped === 0) {
    return rounds;
  }
  return `${rounds} — skipping ${String(skipped)} of the ${String(plan.selected)} selected`;
}
